import { Button, Dropdown, Input, message, Switch, Typography } from "antd"
import TableBox from "../../components/TableBox/TableBox"
import PanelLayout from "../../Layout/PanelLayout"
import { getStoredPageSize } from "../../Utils/TableUtils/TableUtils"
import { useNavigate } from "react-router-dom"
import apiClient from "../../Utils/apiClient"
import { useEffect, useMemo, useState } from "react"
import { MoreOutlined, SearchOutlined } from "@ant-design/icons"
import "../Operations/Operations.scss"
const { Text } = Typography
const Scripts = () => {
  const navigate = useNavigate()
  const page = "Scripts"
  const [data, setData] = useState([])
  const [refresh, setRefresh] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [expandAll, setExpandAll] = useState(false)
  const [search, setSearch] = useState("")
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })

  const fetchData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
      }
      const res = await apiClient("tasks/script/getall", "POST", null, {
        params,
      })

      if (res?.success) {
        const { data: resultData, pageData } = res.result

        setData(resultData)
        setPaginationData((prev) => ({
          ...prev,
          ...pageData,
        }))
      } else {
        message.error("Failed to load Script data")
      }
    } catch (err) {
      console.error(err)
      message.error("Error fetching data!")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [refresh])
  const handleAdd = () => {
    navigate("/add-script")
  }
  const handleEdit = (id) => {
    navigate(`/edit-script/${id}`)
  }

  const handleDelete = async (id) => {
    try {
      const res = await apiClient(`tasks/script/remove-byId/${id}`, "POST")
      if (res.success) {
        message.success(res.message || "Deleted successfully")
        setRefresh((prev) => !prev)
      } else {
        message.error("Failed to delete Script")
      }
    } catch (err) {
      console.error("Delete error:", err)
      message.error("Error deleting scheduler")
    }
  }
  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))
    fetchData(current, pageSize)
  }
  const visibleData = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return data
    return (data || []).filter((row) =>
      [row.title, row.customScript]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    )
  }, [data, search])

  const longScripts = useMemo(
    () => (data || []).filter((row) => (row.customScript || "").length > 200).length,
    [data]
  )

  const kpis = [
    { key: "total", label: "Scripts", value: paginationData.total || data.length, tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
    { key: "page", label: "On this page", value: data.length, tone: "#0891b2", soft: "rgba(56, 189, 248, 0.14)" },
    { key: "long", label: "Long scripts", value: longScripts, tone: "#7c3aed", soft: "rgba(167, 139, 250, 0.14)" },
    { key: "selected", label: "Selected", value: selectedRowKeys.length, tone: "#ea580c", soft: "rgba(234, 88, 12, 0.16)" },
  ]

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 280,
      render: (val, row) => (
        <button
          type="button"
          className="ops-title"
          onClick={() => handleEdit(row._id)}
        >
          <span className="ops-title__name ops-name">{val || "Untitled"}</span>
          <span className="ops-title__kb">
            {(row.customScript || "").split("\n").length} line
            {(row.customScript || "").split("\n").length === 1 ? "" : "s"}
          </span>
        </button>
      ),
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          Custom Script
          <Switch
            size="small"
            checked={expandAll}
            onChange={(checked) => setExpandAll(checked)}
            checkedChildren="Less"
            unCheckedChildren="More"
          />
        </div>
      ),

      dataIndex: "customScript",
      key: "customScript",

      render: (val) => {
        if (!val) return <span className="ops-muted">—</span>

        const isLong = val.length > 20
        const text = !isLong || expandAll ? val : val.slice(0, 50) + "..."
        return (
          <code style={{ whiteSpace: expandAll ? "pre-wrap" : "nowrap", fontSize: 12 }}>
            {text}
          </code>
        )
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "end",
      render: (_, record) => {
        const menuItems = [
          {
            key: "edit",
            label: <span>Edit</span>,
            onClick: () => handleEdit(record._id)
          },
          {
            key: "delete",
            label: <span>Delete</span>,
            onClick: () => handleDelete(record._id)
          },
        ]
        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  const handleBulkDelete = async () => {
    try {
      const data = await apiClient("tasks/script/remove-bulk", "POST", {
        ids: selectedRowKeys,
      })
      if (data?.success) {
        message.success(data?.message)
        setRefresh((pre) => !pre)
        setSelectedRowKeys([])
      }
      if (!data.success) {
        message.info(data?.message)
      }
    } catch (err) {
      console.log("err while remove bulk Scripts", err)
    }
  }
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  }
  return (
    <PanelLayout
      title="Scripts"
      subtitle="Reusable PowerShell and shell scripts you can attach to custom tasks and schedules."
      actions={
        <>
          <Input
            className="ops-search"
            prefix={<SearchOutlined />}
            allowClear
            placeholder="Search title or script (this page)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="primary" onClick={handleAdd}>
            Add Script
          </Button>
        </>
      }
    >
      <div className="ops-kpis">
        {kpis.map((card) => (
          <div
            key={card.key}
            className="ops-kpi ops-kpi--static"
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong>{card.value}</strong>
            </div>
          </div>
        ))}
      </div>

      <div className="ops-toolbar">
        <span className="ops-toolbar__count">
          {paginationData.total || data.length} script
          {(paginationData.total || data.length) === 1 ? "" : "s"}
          {search ? ` · filtered “${search}”` : ""}
          {selectedRowKeys.length ? ` · ${selectedRowKeys.length} selected` : ""}
        </span>
        {selectedRowKeys.length > 0 ? (
          <Button danger onClick={handleBulkDelete}>
            Delete selected ({selectedRowKeys.length})
          </Button>
        ) : null}
      </div>
        <TableBox
          title={<Text strong>Scripts List</Text>}
          className="dashboard-inventory-table"
          page={page}
          columns={columns}
          data={visibleData}
          loading={loading}
          rowSelection={rowSelection}
          shadow={false}
          pagination={{
            current: paginationData.page,
            pageSize: paginationData.pageSize,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
    </PanelLayout>
  )
}
export default Scripts
